// AUTO-EXTRACTED via WebFetch + manual research (Claude Haiku 4.5) on 2026-05-16
// from manufacturer datasheets and SourceSecurity.com product specifications.
//
// Equipment sourced from:
// - Official manufacturer websites (Honeywell, DSC, Visonic, Bosch, Ajax, RISCO, Pyronix)
// - Reseller specifications (SourceSecurity, AlarmGrid, various distributors)
// - GitHub IP camera database (legacy models)
//
// All entries set verified: false. Promote to verified: true after the reviewer
// spot-checks specs against linked datasheets/product pages.
//
// Categories included:
// - Alarm control panels (7 models, 6 manufacturers: Honeywell, DSC, Visonic, Bosch, Ajax, RISCO)
// - NVR recorders (1 model, Dahua)
//
// Note: IP cameras are tracked in a separate system (24,002+ camera SKUs already cataloged).
// This file focuses on security equipment (panels, sensors, recorders, infrastructure).

import type { EquipmentCatalogEntry } from '../equipmentCatalogEntry';

export const BULK_EQUIPMENT_2026_05: EquipmentCatalogEntry[] = [
  // ── HONEYWELL / DSC ────────────────────────────────────────────────────
  {
    id: 'honeywell-vista-20p',
    manufacturer: 'Honeywell Security',
    model: 'VISTA-20P',
    category: 'alarm-panel',
    tagline: '64 zones · 2 partitions · 8 hardwired + 56 wireless + 16 outputs',
    verified: false,
    datasheetUrl: 'https://www.resideo.com/us/en/pro/products/security/intrusion-panels-systems/hybrid-systems/vista-20p-vistar-control-panel-vista-20p/',
    specs: {
      series: 'VISTA',
      zonesOnboard: 8,
      zonesMax: 64,
      partitions: 2,
      outputsOnboard: 8,
      outputsMax: 16,
      users: 48,
      grade: 2,
      communicators: ['PSTN', 'Ethernet'],
      psuAmps: 1.8,
      mobileApp: 'Total Connect',
    },
  },
  {
    id: 'dsc-powerseries-pc1616',
    manufacturer: 'DSC',
    model: 'PowerSeries PC1616',
    category: 'alarm-panel',
    tagline: '16 zones · 2 partitions · 6 onboard + 14 PGM outputs',
    verified: false,
    datasheetUrl: 'https://www.dsc.com/alarm-security-products/PC1616%20-%20PowerSeries%20Control%20Panel%20PC1616/1',
    specs: {
      series: 'PowerSeries',
      zonesOnboard: 6,
      zonesMax: 16,
      partitions: 2,
      outputsOnboard: 2,
      outputsMax: 14,
      users: 48,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE'],
      psuAmps: 0.75,
      mobileApp: 'Alarm.com',
    },
  },
  {
    id: 'dsc-classic-pc1550',
    manufacturer: 'DSC',
    model: 'Classic PC1550',
    category: 'alarm-panel',
    tagline: '6 zones · EOL supervised · 1A siren + 300mA output',
    verified: false,
    datasheetUrl: 'https://docs.johnsoncontrols.com/dsc/v/u/DSC/en-US/PC1500/PC1550-v4.1-Installation-Manual-English-ClassicSeries-6-Zone-Alarm-Control-Panel/1',
    specs: {
      series: 'Classic Series',
      zonesOnboard: 6,
      zonesMax: 6,
      partitions: 1,
      outputsOnboard: 1,
      outputsMax: 1,
      users: 8,
      grade: 2,
      communicators: ['PSTN'],
      psuAmps: 0.5,
    },
  },

  // ── VISONIC ────────────────────────────────────────────────────────────
  {
    id: 'visonic-powermaster-360r-g2',
    manufacturer: 'Visonic',
    model: 'PowerMaster-360R G2',
    category: 'alarm-panel',
    tagline: '64 zones · 120+ wireless devices · PowerG + Z-Wave + Wi-Fi',
    verified: false,
    datasheetUrl: 'https://www.visonic.com/panels/powermaster-panels/powermaster-360r-g2',
    specs: {
      series: 'PowerMaster',
      zonesOnboard: 0,
      zonesMax: 64,
      partitions: 1,
      users: 48,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'Visonic Mobile Manager',
    },
  },

  // ── BOSCH ──────────────────────────────────────────────────────────────
  {
    id: 'bosch-b8512g',
    manufacturer: 'Bosch',
    model: 'B8512G',
    category: 'alarm-panel',
    tagline: '99 points · 8 areas · 500 users · Ethernet + PSTN/GSM modular',
    verified: false,
    datasheetUrl: 'https://www.securityinformed.com/bosch-b8512g-intruder-alarm-system-control-panel-technical-details.html',
    specs: {
      series: 'B-Series',
      zonesOnboard: 8,
      zonesMax: 99,
      partitions: 8,
      outputsOnboard: 3,
      outputsMax: 3,
      users: 500,
      grade: 2,
      communicators: ['Ethernet', 'PSTN', 'GSM-LTE'],
      psuAmps: 2,
    },
  },

  // ── AJAX ───────────────────────────────────────────────────────────────
  {
    id: 'ajax-hub-2',
    manufacturer: 'Ajax Systems',
    model: 'Hub 2',
    category: 'alarm-panel',
    tagline: '100 devices · 50 users · Ethernet + 2G/4G dual SIM · 15h battery',
    verified: false,
    datasheetUrl: 'https://ajax.systems/products/hub-2/',
    specs: {
      series: 'Hub',
      zonesOnboard: 0,
      zonesMax: 100,
      partitions: 9,
      users: 50,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'Ajax',
    },
  },

  // ── RISCO ──────────────────────────────────────────────────────────────
  {
    id: 'risco-lightsys-air',
    manufacturer: 'RISCO Group',
    model: 'LightSYS Air',
    category: 'alarm-panel',
    tagline: '128 radio zones · 4G + LAN + Wi-Fi · Grade 2/3',
    verified: false,
    datasheetUrl: 'https://riscogroup.com/en/rscm_product/lightsys/',
    specs: {
      series: 'LightSYS',
      zonesOnboard: 0,
      zonesMax: 128,
      partitions: 2,
      users: 64,
      grade: 2,
      communicators: ['Ethernet', 'GSM-LTE', 'wireless'],
      mobileApp: 'RisControl Pro',
    },
  },

  // ── NVR RECORDERS ──────────────────────────────────────────────────────
  {
    id: 'dahua-nvr6000',
    manufacturer: 'Dahua Technology',
    model: 'NVR6000',
    category: 'nvr-recorder',
    tagline: '128-channel recorder · RAID · multi-brand ONVIF',
    verified: false,
    datasheetUrl: 'https://www.sourcesecurity.com/dahua-technology-dh-nvr6000-network-video-recorder-nvr-technical-details.html',
    specs: {
      channels: 128,
      hddBays: 2,
      hddBayMaxTb: 8,
      raidLevels: ['0', '1', '5'],
      onvif: true,
      codecs: ['H.264', 'H.265', 'MJPEG'],
    },
  },
];
